import { ThirdPartyErrorMessage } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/third-party/onederful.pb';
import { PayerDetail } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import {
  ErrorDetails,
  ErrorType,
  UnknownErrorMessage,
  ValidationErrorMessage,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import dayjs from 'dayjs';
import Fuse from 'fuse.js';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { formatDateInMonthAndDateWithYear } from '@frontend/date';
import type { TFunction } from '@frontend/i18n';
import { i18next } from '@frontend/i18n';
import { WeaveLocation } from '@frontend/scope';
import { StaticVerticalStepperContent } from '@frontend/static-vertical-stepper';
import { ERROR_MESSAGES, RelationshipEnum } from './constants';
import { Service, BenefitLimit } from './tuuthfairy-types';
import {
  DropdownOption,
  UndefinedOrNull,
  GetPayerDetailsType,
  BenefitLimitNetworkDetails,
  BenefitLimitOverview,
} from './types';

interface NoInsuranceAccessMessageParams {
  accessibleLocationsData: Record<string, WeaveLocation>;
  currentLocationId: string;
  selectedLocationIds: string[];
  t: TFunction;
}
export const getNoInsuranceAccessMessage = ({
  accessibleLocationsData,
  currentLocationId,
  selectedLocationIds,
  t,
}: NoInsuranceAccessMessageParams) => {
  const currentLocationName = accessibleLocationsData[currentLocationId]?.name || '';

  const locationIdsWithAccess = getLocationIdswithInsuranceVerificationAccess(selectedLocationIds);

  const allLocationNames = locationIdsWithAccess
    ?.map((id) => accessibleLocationsData[id]?.name)
    .filter(Boolean)
    .join(', ');

  const message = currentLocationName
    ? t(
        "You don't have permission to view Insurance Verification for the location {{ currentLocationName }}. Please switch to one of the following accessible locations: {{ allLocationNames }}",
        { currentLocationName, allLocationNames }
      )
    : t('You do not have permission to insurance verification for this location');
  return message;
};

export const getPayerList = (payerListResponse: UndefinedOrNull<PayerDetail[]>) => {
  if (!payerListResponse) return [];
  return payerListResponse.map<DropdownOption>((item) => ({
    label: item.name,
    value: item.id,
  }));
};

export const removeNonAlphaNumericCharacters = (value = ''): string => value.replace(/\W/g, '').trim();

export const sanitizeText = (value = ''): string =>
  (value && value.length === 1 ? value.replace('-', '') : value) ?? '';

export const base64ToPdfBlob = (pdfBase64String: string) => {
  const byteString = atob(pdfBase64String);
  const mimeString = 'application/pdf';
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};
const uniquePayerConcatString = '~';

export const getPayerIdAndPayerNameFromCombinedId = (combinedId: string) => {
  const [payerId, payerName] = combinedId.split(uniquePayerConcatString);
  return {
    payerId: payerId ?? '',
    payerName: payerName ?? '',
  };
};

export const getRelationShipValue = (relationship: RelationshipEnum | string): string => {
  switch (relationship.toLowerCase()) {
    case 'self':
    case 'se':
    case RelationshipEnum.Self:
      return i18next.t('Self', { ns: 'insuranceDetails' });
    case 'spouse':
    case 'sp':
    case RelationshipEnum.Spouse:
      return i18next.t('Spouse', { ns: 'insuranceDetails' });
    case 'ch':
    case 'child':
    case RelationshipEnum.Child:
      return i18next.t('Child', { ns: 'insuranceDetails' });
    case 'de':
    case 'dependent':
    case RelationshipEnum.Dependent:
      return i18next.t('Dependent', { ns: 'insuranceDetails' });
    default:
      return i18next.t('Self', { ns: 'insuranceDetails' });
  }
};

export const getPayerCombinedId = (payerId: string, payerName: string): string => {
  return `${payerId}${uniquePayerConcatString}${payerName}`;
};

export function isValidDateByFormat(dateStr: string, format = 'MM/DD/YYYY', isStrictMatch = true): boolean {
  if (!dateStr) return false;
  const parsed = dayjs(dateStr, format, isStrictMatch);
  return parsed.isValid() && parsed.format('MM/DD/YYYY') === dateStr;
}

export const formatDateForDisplay = (date: string, format = 'MM/DD/YYYY') => {
  if (!date) return '';

  try {
    // Check if the date is already in the desired format
    if (isValidDateByFormat(date, format, true)) return date;

    // Extract only the date part
    const dateString = date.includes('T') ? date.split('T')[0] : date;
    if (!dateString) return '';

    return dayjs(dateString).format('MM/DD/YYYY');
  } catch (e) {
    console.error('Error formatting date:', e);
    return date;
  }
};

export const getInsurancePayerDetails = (
  payerID: string,
  payerName: string,
  payerList: Record<'label' | 'value', string>[] = []
): GetPayerDetailsType => {
  if (!payerList.length) return { payerID: '', payerName: '' };

  const filteredInsurancePayerList = payerList.filter((payer) => payer.value === payerID);

  // If only one payer is found, return the payerID since thats the exact match by payer ID
  if (filteredInsurancePayerList.length === 1) {
    return { payerID, payerName: filteredInsurancePayerList[0].label || '' };
  }

  // if multiple payers are found with the same payer ID, return the fuzzy matched payer ID
  if (!!payerName) {
    const fuse = new Fuse(payerList, { keys: ['label'], threshold: 0.2 });
    const searchResult = fuse.search(payerName);

    if (searchResult.length) {
      const fuzzySearchPayerDetails = searchResult.filter(
        (payer) =>
          payer?.item?.value === payerID ||
          payer?.item?.label?.toLowerCase() === payerName?.toLowerCase() ||
          payer?.item?.label?.includes(payerName?.toLowerCase())
      );

      if (fuzzySearchPayerDetails.length) {
        const [payer] = fuzzySearchPayerDetails;
        return { payerID: payer?.item?.value, payerName: payer?.item?.label || '' };
      }
    }
  }

  // if no fuzzy match is found, return the first payer from the filtered list
  if (filteredInsurancePayerList.length) {
    const [payer] = filteredInsurancePayerList;
    return { payerID: payer?.value || '', payerName: payer?.label || '' };
  }

  return { payerID: '', payerName: '' };
};

export const validateNationalProviderId = (value: string): string =>
  value.length !== 10 ? i18next.t('NPI must be 10 characters long', { ns: 'insuranceDetails' }) : '';

export const validateNationalProviderTaxId = (value: string): string => {
  const allowOnlyNumericRegex = /^[0-9]+$/;
  const isNumeric = allowOnlyNumericRegex.test(value);
  const hasMatchingLength = value.length === 9;
  if (!isNumeric) {
    return i18next.t('Only numbers accepted for Tax ID', { ns: 'insuranceDetails' });
  } else if (!hasMatchingLength) {
    return i18next.t('Tax ID must be 9 characters long', { ns: 'insuranceDetails' });
  }
  return '';
};

export const getHoursSavedByInsuranceVerification = (totalNumberOfVerification: number): number => {
  const timeSavedPerVerification = 9; // in minutes
  return Number(((totalNumberOfVerification * timeSavedPerVerification) / 60).toFixed(1));
};

export const getErrorMessageFromDentalCoverageErrorInfo = (
  errorDetails?: ErrorDetails
): { errorMessage: string; errorList: string[] } => {
  let errorMessage = '';
  const errorList: string[] = [];

  if (errorDetails) {
    if (errorDetails.type === ErrorType.ERROR_TYPE_VALIDATION) {
      // SP-TODO: remove this casting once BE update schema type
      const codes = ((errorDetails as any).validationErrorDetails as ValidationErrorMessage)?.codes ?? [];
      codes.forEach((code) => {
        const message = titleCase(code);
        if (message) {
          errorList.push(message);
        }
      });
    }
    if (errorDetails.type === ErrorType.ERROR_TYPE_PARTNER) {
      // SP-TODO: remove this casting once BE update schema type
      const messageInfo = (errorDetails as any).thirdPartyErrorDetails as ThirdPartyErrorMessage;
      messageInfo?.errors?.forEach((error) => {
        const messageKey = ((error.message || error.reason) ?? '').toLowerCase() as keyof typeof ERROR_MESSAGES;
        const message = ERROR_MESSAGES[messageKey] ?? titleCase(messageKey);
        if (message) {
          errorList.push(message);
        }
        const payerSpecificMessage = titleCase(error.payerSpecificMessage ?? '');
        if (payerSpecificMessage) {
          errorList.push(payerSpecificMessage);
        }
      });
      errorMessage = messageInfo?.message ?? '';
    }
    if (errorDetails.type === ErrorType.ERROR_TYPE_UNKNOWN) {
      // SP-TODO: remove this casting once BE update schema type
      errorMessage = ((errorDetails as any).unknownErrorDetails as UnknownErrorMessage)?.message ?? '';
    }
  }
  return { errorMessage, errorList };
};

export const titleCase = (text: string): string => {
  return text
    ? text
        .replace(/([^A-Z])([A-Z])/g, '$1 $2') // for camelCase
        .replace(/[_\\-]+/g, ' ') // for snake_case and lisp-case
        .toLowerCase()
        .replace(/(^\w|\b\w)/g, (m) => m.toUpperCase()) // title case words
        .replace(/\s+/g, ' ') // collapses repeated whitespace
        .replace(/^\s+|\s+$/, '') // removes leading/trailing whitespace
    : '';
};
export const hasInsuranceVerificationAccessForLocation = (locationId: string) =>
  hasSchemaACL(locationId, [Permission.INSURANCE_VERIFICATION_READ, Permission.INSURANCE_VERIFICATION_WRITE]);

export const getLocationIdswithInsuranceVerificationAccess = (locationIds: string[]) => {
  const locationIdsWithAccess = locationIds.filter((locationId) => {
    return hasInsuranceVerificationAccessForLocation(locationId);
  });
  return locationIdsWithAccess;
};

export const groupTuuthFairyServicesDataByDate = (services: Service[]): StaticVerticalStepperContent[] => {
  const groupByDateData = (services || []).reduce((acc, service) => {
    const { category, code, date } = service;
    const formattedDate = formatDateInMonthAndDateWithYear(new Date(date));
    const value = category.trim() !== '' ? category : code;
    if (value) {
      if (!acc[formattedDate]) {
        acc[formattedDate] = {
          title: value,
          subTitle: formattedDate,
        };
      } else {
        acc[formattedDate] = {
          ...acc[formattedDate],
          title: `${acc[formattedDate].title}, ${value}`,
        };
      }
    }
    return acc;
  }, {} as Record<string, StaticVerticalStepperContent>);
  return Object.values(groupByDateData);
};
/**
 * This utility will calculate and provide highest spent record from both the networks
 */
export const getHighestSpentRecords = (benefitLimits: BenefitLimit[]): BenefitLimitNetworkDetails => {
  let highestInNetwork: BenefitLimit | null = null;
  let highestOutNetwork: BenefitLimit | null = null;

  (benefitLimits || []).forEach((record) => {
    const spent = (record.amount || 0) - (record.remaining || 0);

    if (record.networkType === 'in_network') {
      if (!highestInNetwork || spent > (highestInNetwork.amount || 0) - (highestInNetwork.remaining || 0)) {
        highestInNetwork = record;
      }
    } else if (record.networkType === 'out_of_network') {
      if (!highestOutNetwork || spent > (highestOutNetwork.amount || 0) - (highestOutNetwork.remaining || 0)) {
        highestOutNetwork = record;
      }
    }
  });
  return {
    inNetwork: highestInNetwork,
    outOfNetwork: highestOutNetwork,
  };
};

export const toTitleCase = (text: string | undefined, split = '_'): string => {
  if (!text) return '';
  return text
    .split(split)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/*
This utility will decide whether to show benefits limit overview card or not.
The card will be shown only if either in network or outnetwork data is available
*/
export const hasInNetworkOrOutNetworkData = (benefitLimits: BenefitLimitOverview | undefined) => {
  if (benefitLimits) {
    const hasMaximum = !!benefitLimits.maximum.inNetwork || !!benefitLimits.maximum.outOfNetwork;
    const hasDeductible = !!benefitLimits.deductible.inNetwork || !!benefitLimits.deductible.outOfNetwork;
    return hasMaximum || hasDeductible;
  } else {
    return false;
  }
};

//This utility will decide whether to show in network switch or not
export const hasInNetworkData = (benefitLimits: BenefitLimitOverview | undefined) => {
  if (benefitLimits) {
    const hasInNetwork = !!benefitLimits.maximum.inNetwork || !!benefitLimits.deductible.inNetwork;
    return hasInNetwork;
  } else {
    return false;
  }
};

//This utility will decide whether to show out network switch or not
export const hasOutNetworkData = (benefitLimits: BenefitLimitOverview | undefined) => {
  if (benefitLimits) {
    const hasOutNetwork = !!benefitLimits.maximum.outOfNetwork || !!benefitLimits.deductible.outOfNetwork;
    return hasOutNetwork;
  } else {
    return false;
  }
};

export const calculateTuuthFairySpentAmount = (details: BenefitLimit | null) => {
  if (!details) return 0;
  const amount = Number(details?.amount) || 0;
  const remaining = Number(details?.remaining) || 0;
  const spentAmount = amount - remaining;
  //This purely for removing cents 15000 --> 150
  const removeCents = spentAmount / 100;
  return removeCents > 0 ? Math.floor(removeCents) : 0;
};
